import { motion } from "framer-motion";
import { 
  Code2, 
  Shield, 
  Database, 
  Globe, 
  Lock, 
  Smartphone, 
  TrendingUp,
  CheckCircle,
  Users,
  Terminal
} from "lucide-react";

const PlatformTechnicalFeatures = () => {
  const features = [
    {
      icon: Code2,
      title: "Real apps. Not prototypes.",
      description: "Frontend, backend, database, login, and payments — wired up and working on day one. Skip the demo phase, ship the real thing.",
      details: ["Pages that look right on every device", "Backend that handles real users", "Built-in payments", "Production-ready from the first build"],
    },
    {
      icon: Users,
      title: "Engineers behind every line",
      description: "When AI hits a wall, a certified Joylo engineer steps in — already in your codebase, on the same team that built the platform. No freelancers, no handoffs.",
      details: ["In-house, full-time engineers", "Already familiar with your project", "One button to ask for help", "We finish what AI starts"],
    },
    {
      icon: Terminal,
      title: "Use the IDE you already use",
      description: "Developers get full code access in Cursor, VS Code, or anywhere else. Open the terminal, edit a file, push a change — nothing is locked away.",
      details: ["Works with Cursor and VS Code", "Direct terminal access", "Your full source code", "No proprietary runtime"],
    },
    {
      icon: Shield,
      title: "Compliance handled for you",
      description: "SOC 2, GDPR, audit trails, and enterprise-grade security baked in from day one — the boring stuff is already done.",
      details: ["SOC 2 compliant", "GDPR ready", "Audit trails included", "Secure by default"],
    },
    {
      icon: Database,
      title: "Your data is safe and scales with you",
      description: "Real PostgreSQL databases with automatic backups and monitoring — zero setup, zero late-night database emergencies.",
      details: ["Automatic backups", "Scales as you grow", "Monitored 24/7", "Zero setup required"],
    },
    {
      icon: Globe,
      title: "Run it anywhere",
      description: "Launch on AWS, Azure, GCP, or your own cloud. Your code, your choice — no vendor lock-in, ever.",
      details: ["Deploy to any major cloud", "Global delivery network", "Custom domains", "Move anytime"],
    },
    {
      icon: Lock,
      title: "User login and permissions on day one",
      description: "Sign-in, social login, and team permissions work out of the box. Your users can log in before you write a single line of code.",
      details: ["Sign in with Google, GitHub & more", "Teams and roles built in", "Multi-factor authentication", "Sessions handled for you"],
    },
    {
      icon: Smartphone,
      title: "Looks great on every device",
      description: "Every Joylo app feels native on phone, tablet, and desktop — without you thinking about it.",
      details: ["Mobile-first by default", "Works on every screen", "Installable like an app", "Fast on slow connections"],
    },
    {
      icon: TrendingUp,
      title: "Goes from 10 to 10,000 users",
      description: "Your app scales from your first user to your millionth without you touching a server. No DevOps required.",
      details: ["Scales automatically", "Stays fast under load", "Zero downtime deploys", "No servers to manage"],
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section className="py-10 sm:py-20 bg-gradient-to-b from-warm-cream-50 to-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-left sm:text-center mb-8 sm:mb-16"
        >
          <div className="inline-flex items-center gap-2 glass px-3 sm:px-4 py-2 rounded-full mb-5 sm:mb-6 border border-warm-cream-300">
            <Code2 className="w-4 h-4 text-soft-lavender-600" />
            <span className="text-xs sm:text-sm font-medium text-soft-lavender-600">Platform Features</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-ink-blue-900 mb-4 sm:mb-6 leading-[1.15]">
            Built Different. Built for Production.
          </h2>

          <p className="text-base sm:text-lg lg:text-xl text-soft-gray-600 max-w-4xl sm:mx-auto leading-relaxed">
            While others build toys, we build tools. Enterprise-grade applications with human expertise backing every line of code.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{
                y: -8,
                transition: { duration: 0.3 }
              }}
              className="group bg-white rounded-2xl p-6 sm:p-8 border border-warm-cream-200 hover:border-warm-cream-300 hover:shadow-elegant-xl transition-all duration-300"
            >
              {/* Subtle Icon - No gradient background */}
              <div className="mb-4 sm:mb-6">
                <feature.icon className="h-7 w-7 sm:h-8 sm:w-8 text-soft-lavender-500 group-hover:text-sunset-coral-400 transition-colors duration-300" />
              </div>

              {/* Content */}
              <h3 className="text-lg sm:text-xl font-bold text-ink-blue-900 mb-3 sm:mb-4 group-hover:text-ink-blue-800 transition-colors">
                {feature.title}
              </h3>

              <p className="text-soft-gray-600 leading-relaxed mb-4 sm:mb-6 text-sm">
                {feature.description}
              </p>
              
              {/* Feature Details */}
              <div className="space-y-2">
                {feature.details.map((detail, detailIndex) => (
                  <div key={detailIndex} className="flex items-center text-ink-blue-700">
                    <CheckCircle className="w-4 h-4 text-soft-lavender-500 mr-3 flex-shrink-0" />
                    <span className="text-sm font-medium">{detail}</span>
                  </div>
                ))}
              </div>

              {/* Subtle hover indicator */}
              <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="h-1 w-12 bg-gradient-sunset rounded-full"></div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-left sm:text-center mt-8 sm:mt-16"
        >
          <div className="glass rounded-2xl sm:rounded-3xl p-6 sm:p-10 lg:p-12 border border-warm-cream-300">
            <h3 className="text-2xl sm:text-3xl font-bold text-ink-blue-900 mb-3 sm:mb-6 leading-[1.2]">
              Ready to build production-grade applications?
            </h3>
            <p className="text-base sm:text-lg lg:text-xl text-soft-gray-600 mb-6 sm:mb-8 max-w-2xl sm:mx-auto leading-relaxed">
              Join thousands of developers who trust Joylo to deliver enterprise-ready solutions with AI speed and human expertise.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3 sm:gap-4">
              <motion.a
                href="https://joylo.io"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-sunset text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg shadow-elegant hover:shadow-elegant-xl transition-all duration-200 text-center"
              >
                Start Building Today
              </motion.a>
              <motion.a
                href="https://joylo.io"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-warm-cream-300 text-ink-blue-700 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg hover:border-soft-lavender-500 hover:text-soft-lavender-600 transition-all duration-200 text-center"
              >
                View Documentation
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PlatformTechnicalFeatures;